import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Activity, ChevronRight, Loader2, Link2 } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', bg: '#F4F6F9' };
const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const dur = (s) => (s == null ? '—' : s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`);
const when = (iso) => new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function SessionTrail({ session }) {
  return (
    <div className="rounded-sm p-3" style={{ background: C.bg }} data-testid={`activity-session-${session.session_id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] mb-2" style={{ color: C.slate }}>
        <span className="font-mono" style={{ color: C.navy }}>{when(session.started_at)}</span>
        <span>{session.page_views} page view{session.page_views === 1 ? '' : 's'}</span>
        <span>{dur(session.duration_seconds)} on the platform</span>
        <span className="truncate max-w-[280px]" title={session.user_agent}>{session.user_agent?.split(') ')[0]?.replace('(', '· ') || ''}</span>
      </div>
      <ol className="space-y-0.5">
        {session.pages.map((p, i) => (
          <li key={`${p.at}-${i}`} className="flex items-center gap-2 text-[11px]" data-testid="activity-page-row">
            <span className="font-mono w-16 shrink-0" style={{ color: C.slate }}>{new Date(p.at).toLocaleTimeString('en-AU', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
            <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-semibold shrink-0" style={{ background: `${C.blue}14`, color: C.blue }}>{p.section}</span>
            <span className="font-mono truncate" style={{ color: C.navy }}>{p.path}</span>
            <span className="ml-auto shrink-0" style={{ color: C.slate }}>{p.dwell_seconds == null ? 'last page' : dur(p.dwell_seconds)}</span>
            <Link2 className="w-3 h-3 shrink-0" style={{ color: C.green }} title={`sealed · ${p.row_hash?.slice(0, 12)}`} />
          </li>
        ))}
      </ol>
    </div>
  );
}

export const ActivityCard = ({ api }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState('');
  const [trail, setTrail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/visits/users?days=${days}`).then(r => setData(r.data)).catch(() => setData({ users: [], sections: [] }));
  }, [api, days]);
  useEffect(() => { load(); }, [load]);

  const openUser = (email) => {
    if (open === email) { setOpen(''); setTrail(null); return; }
    setOpen(email); setBusy(true); setTrail(null);
    api.get(`/visits/users/${encodeURIComponent(email)}?days=${days}`).then(r => setTrail(r.data)).catch(() => setTrail({ sessions: [] })).finally(() => setBusy(false));
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="activity-card">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Activity className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Session & Section Trail</h3>
        <div className="ml-auto flex rounded-sm overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.25)' }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`activity-days-${d}`}
              className="px-2.5 py-1 text-[11px] font-semibold"
              style={days === d ? { background: C.blue, color: '#fff' } : { color: C.slate, background: '#fff' }}>{d}d</button>
          ))}
        </div>
      </div>
      <p className="text-[11px] mb-3" style={{ color: C.slate }}>
        Which screens each signed-in user opened, session by session. One row per route change, sealed into the
        hash-chained <code>visits</code> collection — it cannot be edited or trimmed. Paths only; no form content.
      </p>
      {!data ? (
        <p className="text-[11px]" style={{ color: C.slate }}>Reading the trail…</p>
      ) : data.users.length === 0 ? (
        <p className="text-[11px]" style={{ color: C.slate }} data-testid="activity-empty">No visits recorded in the last {days} days.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="activity-sections">
            {data.sections.slice(0, 12).map(s => (
              <span key={s.section} className="px-2 py-0.5 rounded-sm text-[10px]" style={{ background: C.bg, color: C.navy }}>
                {s.section} <b style={{ color: C.blue }}>{s.visits}</b> · {s.users} user{s.users === 1 ? '' : 's'}
              </span>
            ))}
          </div>
          <div className="divide-y" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
            {data.users.map(u => (
              <div key={u.email}>
                <button onClick={() => openUser(u.email)} data-testid={`activity-user-${u.email}`}
                  className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-left text-[11px] hover:bg-slate-50">
                  <ChevronRight className="w-3 h-3 transition-transform" style={{ color: C.slate, transform: open === u.email ? 'rotate(90deg)' : 'none' }} />
                  <span className="font-semibold" style={{ color: C.navy }}>{u.name || u.email}</span>
                  <span style={{ color: C.slate }}>{u.email}</span>
                  <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-semibold" style={{ background: `${C.blue}14`, color: C.blue }}>{u.role}</span>
                  <span className="ml-auto" style={{ color: C.slate }}>{u.visits} views · {u.sessions} session{u.sessions === 1 ? '' : 's'}</span>
                  <span className="font-mono" style={{ color: C.navy }}>{ago(u.last_seen)}</span>
                  <span className="font-mono" style={{ color: C.slate }}>{u.last_path}</span>
                </button>
                {open === u.email && (
                  <div className="pb-3 pl-6 space-y-2" data-testid={`activity-trail-${u.email}`}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: C.blue }} /> :
                      (trail?.sessions || []).map(s => <SessionTrail key={s.session_id} session={s} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-[10px] mt-2" style={{ color: C.slate }}>{data.basis}</p>
        </>
      )}
    </Card>
  );
};
